/**
 * Client API for Strapi `planner-event` — one row per user event (name, type, organization JSON).
 * Keep HTTP details here; UI state lives in `usePlannerEvents`.
 */

import { getApiUrl } from '@/lib/api'

const API_URL = getApiUrl()

export type PlannerEventType =
  | 'svadba'
  | 'rodjendan'
  | 'krstenje'
  | 'zurka'
  | 'momacko_vece'
  | 'djevojacko_vece'
  | 'custom'

export interface PlannerEventRecord {
  id: number
  documentId?: string
  name: string
  eventType: PlannerEventType
  customEntries?: string | null
  timelineEntries?: string | null
  createdAt?: string
}

function routeId(id: number, documentId?: string): string | number {
  return documentId != null && documentId !== '' ? documentId : id
}

function parsePlannerEvent(raw: any): PlannerEventRecord {
  const base = raw?.attributes
    ? { id: raw.id, documentId: raw.documentId, ...raw.attributes }
    : raw

  return {
    id: base.id,
    documentId: base.documentId ?? raw?.documentId,
    name: base.name ?? '',
    eventType: (base.eventType || 'svadba') as PlannerEventType,
    customEntries: base.customEntries ?? null,
    timelineEntries: base.timelineEntries ?? null,
    createdAt: base.createdAt,
  }
}

export async function listPlannerEvents(
  token: string,
  userId: number
): Promise<PlannerEventRecord[]> {
  const url = `${API_URL}/api/planner-events?filters[user][id]=${userId}&sort=createdAt:asc`
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (!res.ok) {
    const t = await res.text()
    console.error('listPlannerEvents', res.status, t)
    throw new Error(`Greška pri učitavanju događaja: ${res.status}`)
  }
  const json = await res.json()
  const rows = json.data
  if (!Array.isArray(rows)) return []
  return rows.map(parsePlannerEvent)
}

export async function fetchPlannerEvent(
  token: string,
  id: number,
  documentId?: string
): Promise<PlannerEventRecord> {
  const rid = routeId(id, documentId)
  const res = await fetch(
    `${API_URL}/api/planner-events/${rid}`,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (!res.ok) {
    const t = await res.text()
    console.error('fetchPlannerEvent', res.status, t)
    throw new Error(`Greška pri učitavanju događaja: ${res.status}`)
  }
  const json = await res.json()
  const raw = json.data || json
  return parsePlannerEvent(raw)
}

export async function createPlannerEvent(
  token: string,
  input: {
    userId: number
    name: string
    eventType: PlannerEventType
    customEntries?: string
    timelineEntries?: string
  }
): Promise<PlannerEventRecord> {
  const payload: Record<string, unknown> = {
    name: input.name.trim(),
    eventType: input.eventType,
    user: { id: input.userId },
  }
  if (input.customEntries !== undefined) {
    payload.customEntries = input.customEntries
  }
  if (input.timelineEntries !== undefined) {
    payload.timelineEntries = input.timelineEntries
  }

  const res = await fetch(`${API_URL}/api/planner-events`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ data: payload }),
  })
  if (!res.ok) {
    const t = await res.text()
    console.error('createPlannerEvent', res.status, t)
    throw new Error(`Greška pri kreiranju događaja: ${res.status}`)
  }
  const json = await res.json()
  const raw = json.data || json
  return parsePlannerEvent(raw)
}

export async function patchPlannerEvent(
  token: string,
  id: number,
  documentId: string | undefined,
  data: {
    name?: string
    eventType?: PlannerEventType
    customEntries?: string
    timelineEntries?: string
  }
): Promise<PlannerEventRecord> {
  const body: Record<string, unknown> = {}
  if (data.name !== undefined) body.name = data.name.trim()
  if (data.eventType !== undefined) body.eventType = data.eventType
  if (data.customEntries !== undefined) body.customEntries = data.customEntries
  if (data.timelineEntries !== undefined) {
    body.timelineEntries = data.timelineEntries
  }

  const rid = routeId(id, documentId)
  const res = await fetch(`${API_URL}/api/planner-events/${rid}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ data: body }),
  })
  if (!res.ok) {
    const t = await res.text()
    console.error('patchPlannerEvent', res.status, t)
    throw new Error(`Greška pri čuvanju događaja: ${res.status}`)
  }
  const json = await res.json()
  const raw = json.data || json
  return parsePlannerEvent(raw)
}

export async function deletePlannerEvent(
  token: string,
  id: number,
  documentId?: string
): Promise<void> {
  const rid = routeId(id, documentId)
  const res = await fetch(`${API_URL}/api/planner-events/${rid}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
  if (!res.ok) {
    const t = await res.text()
    console.error('deletePlannerEvent', res.status, t)
    throw new Error(`Greška pri brisanju događaja: ${res.status}`)
  }
}
